// @flow
import { of } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import update from 'immutability-helper';
import routes from '../../routes';
import { endpoints, getFlightApi, getOrderApi, postReserveApi, postPhoneApi } from './api';
import { push } from 'react-router-redux';
import { setNewAlert } from '../alert/duck';
import { actions as AuthActions } from '../auth/duck';
import { fetchAvailableRequestsError } from '../flight/duck';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Flight, Order, Transaction, User } from '../../entities';

type ReserveAction = {
  +type: string;
  +fetchFlightError?: HTTPError;
  +flight?: Flight;
  +fetchOrderError?: HTTPError;
  +order?: Order;
  // Reuse for postReserve and fetch flight
  +requestFlightId?: string;
  +requestOrderId?: string;
  +transactionId?: string;
  +postReserveError?: HTTPError;
  +prefix?: string;
  +phone?: string;
  +enableSmsNotifications?: boolean;
  +error?: HTTPError;
};

export type ReserveState = {
  +isFetchingFlight?: boolean;
  +flight?: Flight;
  +fetchFlightError?: HTTPError;
  +isFetchingOrder?: boolean;
  +order?: Order;
  +fetchOrderError?: HTTPError;
  +isPostingReserve?: boolean;
  +postReserveError?: HTTPError;
  +editSuccessful?: boolean;
  +isPostingPhone?: boolean;
  +postPhoneError?: HTTPError;
};

// Actions
const RESERVE_RESET_STATE = 'RESERVE_RESET_STATE';
const RESERVE_FETCH_ORDER_REQUEST = 'RESERVE_FETCH_ORDER_REQUEST';
const RESERVE_FETCH_ORDER_SUCCESS = 'RESERVE_FETCH_ORDER_SUCCESS';
const RESERVE_FETCH_ORDER_ERROR = 'RESERVE_FETCH_ORDER_ERROR';
const RESERVE_POST_RESERVE_REQUEST = 'RESERVE_POST_RESERVE_REQUEST';
const RESERVE_POST_RESERVE_SUCCESS = 'RESERVE_POST_RESERVE_SUCCESS';
const RESERVE_POST_RESERVE_ERROR = 'RESERVE_POST_RESERVE_ERROR';
const RESERVE_FETCH_FLIGHT_REQUEST = 'RESERVE_FETCH_FLIGHT_REQUEST';
const RESERVE_FETCH_FLIGHT_SUCCESS = 'RESERVE_FETCH_FLIGHT_SUCCESS';
const RESERVE_FETCH_FLIGHT_ERROR = 'RESERVE_FETCH_FLIGHT_ERROR';
const RESERVE_POST_PHONE_REQUEST = 'RESERVE_POST_PHONE_REQUEST';
const RESERVE_POST_PHONE_SUCCESS = 'RESERVE_POST_PHONE_SUCCESS';
const RESERVE_POST_PHONE_ERROR = 'RESERVE_POST_PHONE_ERROR';

// Action Creators
export function resetState(): ReserveAction {
  return {
    type: RESERVE_RESET_STATE,
  };
}

export function fetchFlight(requestFlightId: string): ReserveAction {
  return {
    type: RESERVE_FETCH_FLIGHT_REQUEST,
    requestFlightId,
  };
}

export function fetchFlightSuccess(flight: Flight): ReserveAction {
  return {
    type: RESERVE_FETCH_FLIGHT_SUCCESS,
    flight,
  };
}

export function fetchFlightErrorFn(fetchFlightError: HTTPError): ReserveAction {
  return {
    type: RESERVE_FETCH_FLIGHT_ERROR,
    fetchFlightError,
  }
}

export function fetchOrder(requestFlightId: string, requestOrderId: string): ReserveAction {
  return {
    type: RESERVE_FETCH_ORDER_REQUEST,
    requestFlightId,
    requestOrderId,
  };
}

export function fetchOrderSuccess(order: Order): ReserveAction {
  return {
    type: RESERVE_FETCH_ORDER_SUCCESS,
    order,
  };
}

export function fetchOrderErrorFn(fetchOrderError: HTTPError): ReserveAction {
  return {
    type: RESERVE_FETCH_ORDER_ERROR,
    fetchOrderError,
  }
}

export function postReserve(requestFlightId: string, requestOrderId: string): ReserveAction {
  return {
    type: RESERVE_POST_RESERVE_REQUEST,
    requestFlightId,
    requestOrderId,
  };
}

export function postReserveSuccess(transactionId: string): ReserveAction {
  return {
    type: RESERVE_FETCH_ORDER_SUCCESS,
    transactionId,
  };
}

export function postReserveErrorFn(postReserveError: HTTPError): ReserveAction {
  return {
    type: RESERVE_POST_RESERVE_ERROR,
    postReserveError,
  };
}

export function postPhone(phone: string, prefix: string, enableSmsNotifications: boolean): ReserveAction {
  return {
    type: RESERVE_POST_PHONE_REQUEST,
    phone,
    prefix,
    enableSmsNotifications,
  };
}

export function postPhoneSuccess(): ReserveAction {
  return {
    type: RESERVE_POST_PHONE_SUCCESS,
  };
}

export function postPhoneError(error: HTTPError): ReserveAction {
  return {
    type: RESERVE_POST_PHONE_ERROR,
    error,
  };
}

export const actions = {
  RESERVE_RESET_STATE,
  RESERVE_FETCH_FLIGHT_REQUEST,
  RESERVE_FETCH_FLIGHT_SUCCESS,
  RESERVE_FETCH_FLIGHT_ERROR,
  RESERVE_FETCH_ORDER_REQUEST,
  RESERVE_FETCH_ORDER_SUCCESS,
  RESERVE_FETCH_ORDER_ERROR,
  RESERVE_POST_RESERVE_REQUEST,
  RESERVE_POST_RESERVE_SUCCESS,
  RESERVE_POST_RESERVE_ERROR,
  RESERVE_POST_PHONE_REQUEST,
  RESERVE_POST_PHONE_SUCCESS,
  RESERVE_POST_PHONE_ERROR,
  resetState,
  fetchFlight,
  fetchFlightSuccess,
  fetchFlightErrorFn,
  fetchOrder,
  fetchOrderSuccess,
  fetchOrderErrorFn,
  postReserve,
  postReserveSuccess,
  postReserveErrorFn,
  postPhone,
  postPhoneSuccess,
  postPhoneError,
};

export const initialState: ReserveState = {};

// Reducer
export default function reducer(state: ReserveState = initialState,
                                action: ReserveAction): ReserveState {
  switch (action.type) {
    case RESERVE_FETCH_FLIGHT_REQUEST:
      return update(state, {
        isFetchingFlight: { $set: true },
      });
    case RESERVE_FETCH_FLIGHT_SUCCESS:
      return update(state, {
        $unset: ['isFetchingFlight'],
        flight: { $set: action.flight },
      });
    case RESERVE_FETCH_FLIGHT_ERROR:
      return update(state, {
        $unset: ['isFetchingFlight'],
        fetchFlightError: { $set: action.fetchFlightError },
      });
    case RESERVE_FETCH_ORDER_REQUEST:
      return update(state, {
        isFetchingOrder: { $set: true },
      });
    case RESERVE_FETCH_ORDER_SUCCESS:
      return update(state, {
        $unset: ['isFetchingOrder'],
        order: { $set: action.order },
      });
    case RESERVE_FETCH_ORDER_ERROR:
      return update(state, {
        $unset: ['isFetchingOrder'],
        fetchOrderError: { $set: action.fetchOrderError },
      });
    case RESERVE_POST_RESERVE_REQUEST:
      return update(state, {
        $unset: ['editSuccessful'],
        isPostingReserve: { $set: true },
      });
    case RESERVE_POST_RESERVE_SUCCESS:
      return update(state, {
        $unset: ['isPostingReserve'],
      });
    case RESERVE_POST_RESERVE_ERROR:
      return update(state, {
        $unset: ['isPostingReserve'],
        postReserveError: { $set: action.postReserveError },
      });
    case RESERVE_RESET_STATE:
      return initialState;
    case RESERVE_POST_PHONE_REQUEST:
      return update(state, {
        isPostingPhone: { $set: true },
      });
    case RESERVE_POST_PHONE_SUCCESS:
      return update(state, {
        $unset: ['isPostingPhone'],
        editSuccessful: { $set: true },
      });
    case RESERVE_POST_PHONE_ERROR:
      return update(state, {
        $unset: ['isPostingPhone'],
        error: { $set: action.error },
      });
    default:
      return state;
  }
}

// Epics
export function fetchReserveFlightEpic(action$: Observable<ReserveAction>): Observable<ReserveAction> {
  return action$.pipe(
    ofType(RESERVE_FETCH_FLIGHT_REQUEST),
    mergeMap(({ requestFlightId }) => getFlightApi(undefined, {
        route: endpoints.GET_FLIGHT.replace(':id', requestFlightId),
      }).pipe(
      map((response: Flight) => fetchFlightSuccess(response)),
      catchError((e: HTTPError) => of(fetchFlightErrorFn(e)))),
    ));
}

export function fetchReserveOrderEpic(action$: Observable<ReserveAction>): Observable<ReserveAction> {
  return action$.pipe(
    ofType(RESERVE_FETCH_ORDER_REQUEST),
    mergeMap(({ requestFlightId, requestOrderId }) => getOrderApi(undefined, {
        route: endpoints.GET_ORDER
          .replace(':idOrder', requestOrderId)
          .replace(':idFlight', requestFlightId),
      }).pipe(
      map((response: Order) => fetchOrderSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(
            setNewAlert('reserve', 'error', undefined, 'httpErrors.fetch.500'),
            fetchOrderErrorFn(e),
          );
        } else if (Math.round(e.code / 100) === 4) {
          if (!!e.data && e.data.name === 'OrderAlreadyBooked') {
            return of(
              fetchOrderErrorFn(e),
              fetchAvailableRequestsError(e),
              push(routes.FLIGHT.children.REQUESTS.routePath.replace(':id', requestFlightId)),
            );
          }
          return of(
            setNewAlert('reserve', 'error', undefined, 'httpErrors.fetch.400'),
            fetchOrderErrorFn(e),
          );
        }
        return of(fetchOrderErrorFn(e));
      })),
    ));
}

export function postReserveEpic(action$: Observable<ReserveAction>): Observable<ReserveAction> {
  return action$.pipe(
    ofType(RESERVE_POST_RESERVE_REQUEST),
    mergeMap(({ requestFlightId, requestOrderId }) =>
      postReserveApi(undefined, {
        route: endpoints.POST_RESERVE
          .replace(':idFlight', requestFlightId)
          .replace(':idOrder', requestOrderId),
      }).pipe(
        mergeMap((transaction: Transaction) => of(
          postReserveSuccess(transaction),
          setNewAlert('reserve', 'success', undefined, 'reserve.successCreate'),
          push(`/sale/order/${transaction.id}`),
        )),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(
              setNewAlert('reserve', 'error', undefined, 'httpErrors.post.500'),
              postReserveErrorFn(e),
            );
          } else if (Math.round(e.code / 100) === 4) {
            let message = 'reserve.post.400.invalidState';
            if (typeof e.data === 'object' && e.data.PropertyValidationError) {
              message = 'reserve.post.400.missingPhone';
            }
            return of(
              setNewAlert('reserve', 'error', undefined, message),
              postReserveErrorFn(e),
            );
          }
          return of(postReserveErrorFn(e));
        })),
    ));
}

export function updatePhoneEpic(action$: Observable<ReserveAction>): Observable<ReserveAction> {
  return action$.pipe(
    ofType(RESERVE_POST_PHONE_REQUEST),
    mergeMap(({ phone, prefix, enableSmsNotifications }) =>
      postPhoneApi({ phone, phoneCountry: prefix, enableSmsNotifications }).pipe(
        mergeMap((user: User) =>
          // order matters, when you get to ComponentDidUpdate you will have the user correctly updated
          of(AuthActions.updateUser({ phone, phoneCountry: user.phoneCountry }), postPhoneSuccess())),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postPhoneError(e),
              setNewAlert('reserve', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postPhoneError(e),
              setNewAlert('reserve', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postPhoneError(e));
        })),
    ));
}



// WEBPACK FOOTER //
// ./src/modules/reserve/duck.js